import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  TrendingUp,
  Security,
  Warning,
  CheckCircle,
  Timeline,
  Refresh,
  Download,
  Block,
  Person,
  InsightsOutlined,
  SearchOutlined,
  GroupOutlined,
  MemoryOutlined,
  CloudDownloadOutlined,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'react-hot-toast';
import { useGuardrailAnalytics } from '../../hooks/useAnalytics.ts';
import LoadingPopup from '../misc/Loading.js';
import API from '../../services/api';


const GuardRailsAnalysis = () => {
  const {
    guardrailData,
    hardwareData,
    loading,
    error,
    fetchAllData,
  } = useGuardrailAnalytics();
  
  const intervalRef = useRef();
  const [timeRange, setTimeRange] = useState(24);


  const [detectorStats, setDetectorStats] = useState([]);

    const fetchAndLogAnalytics = useCallback(() => {
    API.get('/guardrails-analytics/')
      .then(({ data }) => {
        setDetectorStats(data.detector_stats || []);
      })
      .catch(err => {
        console.error('Error fetching guardrails analytics:', err);
      });
  }, []);

    useEffect(() => {
    fetchAndLogAnalytics();
    intervalRef.current = setInterval(fetchAndLogAnalytics, 5 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [fetchAndLogAnalytics]);

  const getBlockRateColor = (rate) => {
    if (rate < 5) return 'success.main';
    if (rate < 15) return 'warning.main';
    return 'error.main';
  };


  const downloadSummaryJson = () => {
    if (!guardrailData) return;
    
    const summaryData = {
      timestamp: new Date().toISOString(),
      timeRange: `${timeRange} hours`,
      summary: {
        total_requests: guardrailData.total_requests,
        blocked_requests: guardrailData.blocked_requests,
        allowed_requests: guardrailData.allowed_requests,
        block_rate: guardrailData.block_rate,
        unique_users: guardrailData.user_analytics?.unique_users || 0
      },
      top_blocked_categories: guardrailData.top_blocked_categories,
      detector_status: Object.keys(guardrailData.detector_performance || {}).map(detector => {
        const perf = guardrailData.detector_performance[detector];
        return {
          name: detector,
          enabled: perf?.enabled || false,
          accuracy: perf?.accuracy || 0
        };
      })
    };
    
    const blob = new Blob([JSON.stringify(summaryData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guardrail-summary-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Summary JSON downloaded successfully');
  };

  const downloadDetailedJson = () => {
    if (!guardrailData) return;
    
    const detailedData = {
      timestamp: new Date().toISOString(),
      timeRange: `${timeRange} hours`,
      guardrail_metrics: guardrailData,
      hardware_metrics: hardwareData,
      export_metadata: {
        exported_by: 'Guardrail Analytics Dashboard',
        version: '1.0.0',
        format: 'detailed'
      }
    };
    
    const blob = new Blob([JSON.stringify(detailedData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guardrail-detailed-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Detailed JSON downloaded successfully');
  };

  const downloadFormattedExcel = () => {
    if (!guardrailData) return;
    
    // Create CSV data that can be opened in Excel
    const csvData = [
      ['Guardrail Analytics Report'],
      ['Generated:', new Date().toISOString()],
      ['Time Range:', `${timeRange} hours`],
      [''],
      ['SUMMARY METRICS'],
      ['Metric', 'Value'],
      ['Total Requests', guardrailData.total_requests],
      ['Blocked Requests', guardrailData.blocked_requests],
      ['Allowed Requests', guardrailData.allowed_requests],
      ['Block Rate (%)', guardrailData.block_rate.toFixed(2)],
      [''],
      ['DETECTOR PERFORMANCE'],
      ['Detector', 'Enabled', 'Request Count'],
      ...Object.entries(guardrailData.detector_performance || {}).map(([detector, perf]) => [
        detector,
        perf.enabled ? 'Yes' : 'No',
        perf.request_count
      ]),
      [''],
      ['TOP BLOCKED CATEGORIES'],
      ['Category', 'Count'],
      ...Object.entries(guardrailData.top_blocked_categories || {}).map(([category, count]) => [category, count]),
      [''],
      ['USER ANALYTICS'],
      ['Metric', 'Value'],
      ['Unique Users', guardrailData.user_analytics?.unique_users || 0],
      ['Repeat Violators', guardrailData.user_analytics?.repeat_violators || 0],
      ['New Users Today', guardrailData.user_analytics?.new_users_today || 0]
    ];

    if (hardwareData) {
      csvData.push(
        [''],
        ['HARDWARE METRICS'],
        ['Metric', 'Value'],
        ['CPU Usage (%)', hardwareData.cpu_usage.toFixed(1)],
        ['Memory Usage (%)', hardwareData.memory_usage.toFixed(1)],
        ['Disk Usage (%)', hardwareData.disk_usage.toFixed(1)],
        ['System Temperature (°C)', hardwareData.temperature.toString()]
      );
    }
    
    const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guardrail-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Excel-formatted CSV downloaded successfully');
  };

  if (loading && !guardrailData) {
    return <LoadingPopup isVisible={true} />;
  }

  if (error && !guardrailData) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error}
        <Button onClick={() => fetchAllData(timeRange)} sx={{ ml: 2 }}>Retry</Button>
      </Alert>
    );
  }
  console.log("detectorStats", detectorStats);

  return (
    <Box sx={{ p: 3, backgroundColor: '#101012', minHeight: '100vh', color: '#ffffff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#ffffff', fontWeight: 600 }}>
          Guardrail Analytics Dashboard
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl 
            size="small"
            sx={{
              '& .MuiInputLabel-root': {
                color: '#b0b0b0',
                '&.Mui-focused': {
                  color: '#ffffff'
                }
              },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': {
                  borderColor: '#2a2a3e'
                },
                '&:hover fieldset': {
                  borderColor: '#ffffff'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00D4AA'
                }
              },
              '& .MuiSelect-icon': {
                color: '#b0b0b0'
              }
            }}
          >
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(Number(e.target.value))}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#101012',
                    border: '1px solid #2a2a3e',
                    '& .MuiMenuItem-root': {
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 212, 170, 0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 212, 170, 0.25)'
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value={1}>Last Hour</MenuItem>
              <MenuItem value={6}>Last 6 Hours</MenuItem>
              <MenuItem value={24}>Last 24 Hours</MenuItem>
              <MenuItem value={168}>Last Week</MenuItem>
            </Select>
          </FormControl>

          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchAllData(timeRange);
              fetchAndLogAnalytics();
            }}
            disabled={loading}
            sx={{
              borderColor: '#2a2a3e',
              color: '#ffffff',
              '&:hover': {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              },
              '&:disabled': {
                borderColor: '#2a2a3e',
                color: '#b0b0b0'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Single Page Analytics - All Content */}
      {guardrailData && (
        <Box>
          {/* Section 1: Key Metrics Overview */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: 3,
              color: '#ffffff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <InsightsOutlined
              sx={{
                color: '#00D4AA',
                fontSize: 32,
                filter: 'drop-shadow(0 0 8px #00D4AA88)',
              }}
            />
            System Overview
          </Typography>

          {/* Key Metrics */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
            <Box>
              <Card sx={{ backgroundColor: 'rgba(76, 175, 80, 0.15)', border: '1px solid rgba(76, 175, 80, 0.3)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <CheckCircle color="success" fontSize="large" />
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {guardrailData.allowed_requests.toLocaleString()}
                      </Typography>
                                              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                          Allowed Requests
                        </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card sx={{ backgroundColor: 'rgba(244, 67, 54, 0.15)', border: '1px solid rgba(244, 67, 54, 0.3)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Block color="error" fontSize="large" />
                    <Box>
                      <Typography variant="h4" color="error.main">
                        {guardrailData.blocked_requests.toLocaleString()}
                      </Typography>
                                              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                          Blocked Requests
                        </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card sx={{ backgroundColor: 'rgba(33, 150, 243, 0.15)', border: '1px solid rgba(33, 150, 243, 0.3)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Timeline color="primary" fontSize="large" />
                    <Box>
                      <Typography variant="h4" color="primary.main">
                        {guardrailData.total_requests.toLocaleString()}
                      </Typography>
                                              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                          Total Requests
                        </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card sx={{ backgroundColor: 'rgba(255, 152, 0, 0.15)', border: '1px solid rgba(255, 152, 0, 0.3)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Security color="warning" fontSize="large" />
                    <Box>
                      <Typography variant="h4" sx={{ color: getBlockRateColor(guardrailData.block_rate) }}>
                        {guardrailData.block_rate.toFixed(1)}%
                      </Typography>
                                              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                          Guardrail Activation Rate
                        </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>


          {/* Detection Categories Chart */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Card sx={{ backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>Top Blocked Categories</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={Object.entries(guardrailData.top_blocked_categories || {}).map(([name, value]) => ({ name, value }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={25}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent ? (percent * 100).toFixed(0) : 0)}%`}
                        labelStyle={{ fill: '#ffffff', fontSize: '12px', fontWeight: '500' }}
                      >
                        {Object.entries(guardrailData.top_blocked_categories || {}).map(([key], index) => {
                          // Dark, minimalistic, futuristic color palette
                          const darkThemeColors = [
                            '#00D4AA', // Primary brand teal
                            '#4A90E2', // Muted blue
                            '#7B68EE', // Muted purple
                            '#50C878', // Muted emerald
                            '#FF6B9D', // Muted pink
                            '#F39C12', // Muted orange
                            '#8E44AD', // Deep purple
                            '#2ECC71'  // Muted green
                          ];
                          
                          return (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={darkThemeColors[index % darkThemeColors.length]}
                              stroke="rgba(255, 255, 255, 0.1)"
                              strokeWidth={1}
                              style={{
                                filter: 'drop-shadow(0 0 8px rgba(0, 212, 170, 0.3))',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.filter = 'drop-shadow(0 0 15px rgba(0, 212, 170, 0.6)) brightness(1.2)';
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.filter = 'drop-shadow(0 0 8px rgba(0, 212, 170, 0.3))';
                                e.target.style.transform = 'scale(1)';
                              }}
                            />
                          );
                        })}
                      </Pie>
                      <RechartsTooltip 
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #2a2a3e',
                          borderRadius: '8px',
                          color: '#ffffff',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                        }}
                          labelStyle={{
                          color: '#00D4AA', // or any accent color you prefer
                          fontWeight: 600,
                          fontSize: 15,
                          textShadow: '0 0 6px #00D4AA44',
                        }}
                        itemStyle={{
                          color: '#fff',
                          fontWeight: 500,
                          fontSize: 14,
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card sx={{ backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>Detection Timeline (24h)</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={guardrailData.hourly_stats || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                      <XAxis 
                        dataKey="hour" 
                        axisLine={{ stroke: '#2a2a3e' }}
                        tickLine={{ stroke: '#2a2a3e' }}
                        tick={{ fill: '#b0b0b0', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={{ stroke: '#2a2a3e' }}
                        tickLine={{ stroke: '#2a2a3e' }}
                        tick={{ fill: '#b0b0b0', fontSize: 12 }}
                      />
                      <RechartsTooltip 
                        contentStyle={{
                          backgroundColor: '#101012',
                          border: '1px solid #2a2a3e',
                          borderRadius: '8px',
                          color: '#ffffff',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                        }}
                        labelStyle={{ color: '#ffffff' }}
                      />
                      <Legend 
                        wrapperStyle={{ color: '#ffffff' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="blocked" 
                        stackId="1" 
                        stroke="#FF6B9D" 
                        fill="rgba(255, 107, 157, 0.3)" 
                        name="Blocked"
                        strokeWidth={2}
                        dot={{ fill: '#FF6B9D', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, fill: '#FF6B9D', stroke: '#ffffff', strokeWidth: 2 }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="allowed" 
                        stackId="1" 
                        stroke="#00D4AA" 
                        fill="rgba(0, 212, 170, 0.3)" 
                        name="Allowed"
                        strokeWidth={2}
                        dot={{ fill: '#00D4AA', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, fill: '#00D4AA', stroke: '#ffffff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Box>
          </Box>
          
          {/* Section 2: Content Analysis */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mt: 6,
              mb: 3,
              color: '#ffffff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <SearchOutlined
              sx={{
                color: '#4A90E2',
                fontSize: 32,
                filter: 'drop-shadow(0 0 8px #4A90E288)',
              }}
            />
            Content Analysis
          </Typography>
          
          {guardrailData?.content_analysis && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 6 }}>
              <Box>
                <Card sx={{ backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', mb: 3 }}>Most Flagged Words</Typography>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
                      {Object.entries(guardrailData.content_analysis.common_words || {})
                        .slice(0, 8)
                        .map(([word, count], index) => (
                          <Box 
                            key={word} 
                            display="flex" 
                            justifyContent="space-between" 
                            alignItems="center"
                            sx={{ 
                              py: 1.5, 
                              px: 2, 
                              backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
                              borderRadius: 1,
                              border: '1px solid rgba(255, 255, 255, 0.05)'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#ffffff' }}>{word}</Typography>
                            <Chip 
                              label={count} 
                              size="small" 
                              sx={{
                                backgroundColor: '#FF6B9D',
                                color: '#ffffff',
                                fontWeight: 600,
                                '&.MuiChip-colorError': {
                                  backgroundColor: '#FF6B9D',
                                  color: '#ffffff'
                                }
                              }}
                            />
                          </Box>
                        ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box>
                <Card sx={{ backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', mb: 3 }}>Detector Settings</Typography>
                  <TableContainer sx={{ flex: 1 }}>
                    <Table sx={{ 
                      '& .MuiTableCell-root': { 
                        color: '#ffffff', 
                        borderColor: '#2a2a3e',
                        py: 2
                      },
                      '& .MuiTableHead .MuiTableCell-root': {
                        fontSize: '0.875rem',
                        fontWeight: 600
                      },
                      '& .MuiTableBody .MuiTableRow-root': {
                        height: '60px'
                      }
                    }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Detector</TableCell>
                          <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 600 }}>Volume</TableCell>
                          <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {(detectorStats.length ? detectorStats || [] : []).map((detectorObj, idx) => {
                        const detector = Object.keys(detectorObj)[0];
                        const enabled = detectorObj[detector];
                        return (
                          <TableRow key={detector} sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
                            <TableCell component="th" scope="row" sx={{ color: '#ffffff' }}>
                              <Chip 
                                label={detector.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                size="small"
                                color={enabled ? 'primary' : 'default'}
                                sx={{
                                  backgroundColor: enabled ? '#00D4AA' : '#2a2a3e',
                                  color: enabled ? '#ffffff' : '#b0b0b0',
                                  '&.MuiChip-colorPrimary': {
                                    backgroundColor: '#00D4AA',
                                    color: '#ffffff'
                                  },
                                  '&.MuiChip-colorDefault': {
                                    backgroundColor: '#2a2a3e',
                                    color: '#b0b0b0'
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#ffffff' }}>—</TableCell>
                            <TableCell align="center" sx={{ color: '#ffffff' }}>
                              {enabled ? (
                                <CheckCircle color="success" fontSize="small" />
                              ) : (
                                <Warning color="error" fontSize="small" />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    </Table>
                  </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          {/* Section 3: User Analytics */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: 3,
              color: '#ffffff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <GroupOutlined
              sx={{
                color: '#FF6B9D',
                fontSize: 32,
                filter: 'drop-shadow(0 0 8px #FF6B9D88)',
              }}
            />
            User Analytics
          </Typography>

          {guardrailData?.user_analytics && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 6 }}>
              <Box>
                <Card sx={{ backgroundColor: 'rgba(33, 150, 243, 0.15)', border: '1px solid rgba(33, 150, 243, 0.3)', backdropFilter: 'blur(10px)' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Person color="primary" fontSize="large" />
                      <Box>
                        <Typography variant="h4" color="primary.main">
                          {guardrailData.user_analytics.unique_users}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                          Unique Users
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box>
                <Card sx={{ backgroundColor: 'rgba(244, 67, 54, 0.15)', border: '1px solid rgba(244, 67, 54, 0.3)', backdropFilter: 'blur(10px)' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Warning color="error" fontSize="large" />
                      <Box>
                        <Typography variant="h4" color="error.main">
                          {guardrailData.user_analytics.repeat_violators}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                          Repeat Violators
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box>
                <Card sx={{ backgroundColor: 'rgba(76, 175, 80, 0.15)', border: '1px solid rgba(76, 175, 80, 0.3)', backdropFilter: 'blur(10px)' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TrendingUp color="success" fontSize="large" />
                      <Box>
                        <Typography variant="h4" color="success.main">
                          {guardrailData.user_analytics.new_users_today}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                          New Users Today
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          {/* User Activity Table */}
          <Card sx={{ mb: 6, backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffffff' }}>
                <Person />
                User Activity
              </Typography>
              <TableContainer>
                <Table sx={{ '& .MuiTableCell-root': { color: '#ffffff', borderColor: '#2a2a3e' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>IP Address</TableCell>
                      <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 600 }}>Requests</TableCell>
                      <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 600 }}>Blocked</TableCell>
                      <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 600 }}>Guardrail Activation Rate</TableCell>
                      <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 600 }}>Last Seen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { ip_address: '192.168.1.1', total_requests: 100, blocked_requests: 10, block_rate: 10, last_seen: Date.now() },
                      { ip_address: '10.0.0.15', total_requests: 85, blocked_requests: 15, block_rate: 17.6, last_seen: Date.now() - 3600000 },
                      { ip_address: '172.16.0.42', total_requests: 120, blocked_requests: 8, block_rate: 6.7, last_seen: Date.now() - 7200000 },
                      { ip_address: '203.0.113.7', total_requests: 65, blocked_requests: 12, block_rate: 18.5, last_seen: Date.now() - 10800000 },
                      { ip_address: '198.51.100.23', total_requests: 95, blocked_requests: 5, block_rate: 5.3, last_seen: Date.now() - 14400000 }
                    ].map((user, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
                        <TableCell sx={{ color: '#ffffff' }}>{user.ip_address}</TableCell>
                        <TableCell align="right" sx={{ color: '#ffffff' }}>{user.total_requests}</TableCell>
                        <TableCell align="right" sx={{ color: '#ffffff' }}>{user.blocked_requests}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${user.block_rate}%`} 
                            size="small" 
                            color={user.block_rate > 15 ? 'error' : user.block_rate > 10 ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#ffffff' }}>
                          {user.last_seen ? new Date(user.last_seen).toLocaleString() : 'Unknown'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Section 4: Hardware Status */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mt: 6,
              mb: 3,
              color: '#ffffff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <MemoryOutlined
              sx={{
                color: '#F39C12',
                fontSize: 32,
                filter: 'drop-shadow(0 0 8px #F39C1288)',
              }}
            />
            System Hardware
          </Typography>

          {hardwareData ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 6 }}>
              <Box>
                <Card sx={{ backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', mb: 3 }}>System Resources</Typography>
                    
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
                      <Box sx={{ mb: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>CPU Usage</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: hardwareData.cpu_usage > 80 ? '#f44336' : '#00D4AA' }}>
                            {hardwareData.cpu_usage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={hardwareData.cpu_usage} 
                          color={hardwareData.cpu_usage > 80 ? 'error' : 'primary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Memory Usage</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: hardwareData.memory_usage > 80 ? '#f44336' : '#00D4AA' }}>
                            {hardwareData.memory_usage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={hardwareData.memory_usage} 
                          color={hardwareData.memory_usage > 80 ? 'error' : 'primary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      {hardwareData.gpu_usage && (
                        <Box sx={{ mb: 3 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>GPU Usage</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: hardwareData.gpu_usage > 80 ? '#f44336' : '#00D4AA' }}>
                              {hardwareData.gpu_usage.toFixed(1)}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={hardwareData.gpu_usage} 
                            color={hardwareData.gpu_usage > 80 ? 'error' : 'success'}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                      )}

                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Disk Usage</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: hardwareData.disk_usage > 90 ? '#f44336' : '#00D4AA' }}>
                            {hardwareData.disk_usage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={hardwareData.disk_usage} 
                          color={hardwareData.disk_usage > 90 ? 'error' : 'primary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box>
                <Card sx={{ backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', mb: 3 }}>Model Performance</Typography>
                    
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 2, fontWeight: 500 }}>
                          Model Load Times (ms)
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {Object.entries(hardwareData.model_load_times || {}).map(([model, time]) => {
                            const timeValue = time;
                            return (
                              <Box key={model} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{model}</Typography>
                                <Chip 
                                  label={`${timeValue}ms`} 
                                  size="small" 
                                  color={timeValue > 1000 ? 'error' : timeValue > 500 ? 'warning' : 'success'}
                                  sx={{
                                    fontWeight: 600,
                                    '&.MuiChip-colorSuccess': {
                                      backgroundColor: '#00D4AA',
                                      color: '#ffffff'
                                    }
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 2, fontWeight: 500 }}>
                          Network I/O
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, backgroundColor: 'rgba(0, 212, 170, 0.1)', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#b0b0b0', display: 'block' }}>Sent</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#00D4AA' }}>
                              {(hardwareData.network_io.sent / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, backgroundColor: 'rgba(74, 144, 226, 0.1)', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#b0b0b0', display: 'block' }}>Received</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                              {(hardwareData.network_io.received / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 1.5, fontWeight: 500 }}>System Temperature</Typography>
                        <Chip 
                          label={`${Math.round(hardwareData.temperature)}°C`} 
                          size="medium" 
                          color={hardwareData.temperature > 70 ? 'error' : 'success'}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            '&.MuiChip-colorSuccess': {
                              backgroundColor: '#00D4AA',
                              color: '#ffffff'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 6 }}>
              Hardware monitoring not available. Enable system monitoring for detailed hardware metrics.
            </Alert>
          )}

          {/* Section 6: Download Options */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mt: 6,
              mb: 3,
              color: '#ffffff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CloudDownloadOutlined
              sx={{
                color: '#10b981',
                fontSize: 32,
                filter: 'drop-shadow(0 0 8px #10b98188)',
              }}
            />
            Export Analytics
          </Typography>

          <Card sx={{ mb: 6, backgroundColor: '#101012', border: '1px solid #2a2a3e', color: '#ffffff' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>Download Analytics Data</Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#b0b0b0' }}>
                Export your analytics data in various formats for reporting and analysis.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => downloadSummaryJson()}
                  sx={{
                    borderColor: '#00D4AA',
                    color: '#00D4AA',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 212, 170, 0.1)',
                      borderColor: '#00D4AA',
                    },
                  }}
                >
                  Summary JSON
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => downloadDetailedJson()}
                  sx={{
                    borderColor: '#3b82f6',
                    color: '#3b82f6',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      borderColor: '#3b82f6',
                    },
                  }}
                >
                  Detailed JSON
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => downloadFormattedExcel()}
                  sx={{
                    borderColor: '#10b981',
                    color: '#10b981',
                    '&:hover': {
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      borderColor: '#10b981',
                    },
                  }}
                >
                  Formatted Excel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default GuardRailsAnalysis;
